import { useRef, useState } from 'react';
import Button from 'shared/ui/button/button';
import { Icon } from 'shared/ui/icon/icon';
import { ICONS } from 'shared/constants/images';
import { useTranslation, useCachedSelectedProject } from 'hooks';
import {
  useGetExportConfigQuery,
  useSaveExportConfigMutation,
} from 'store/api/pdfReport/pdfReportApiSlice';
import cls from './logo-import.module.scss';

const ACCEPTED_TYPES = ['image/jpeg', 'image/png'];
const MAX_FILE_SIZE_BYTES = 4 * 1024 * 1024;
const MAX_DIMENSION_PX = 5000;

const getImageDimensions = (file: File) =>
  new Promise<{ width: number; height: number }>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('image_load_error'));
    };
    img.src = url;
  });

const readFileAsBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(new Error('file_read_error'));
    reader.readAsDataURL(file);
  });

export const LogoImport: React.FC = () => {
  const { t } = useTranslation();
  const { domainId } = useCachedSelectedProject();
  const inputRef = useRef<HTMLInputElement>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [pendingAction, setPendingAction] = useState<'save' | 'remove' | null>(
    null
  );

  const { data: config } = useGetExportConfigQuery(domainId ?? '', {
    skip: !domainId,
  });
  const [saveExportConfig] = useSaveExportConfigMutation();

  const previewUrl = config?.logo
    ? `data:${config.logo.mediaType};base64,${config.logo.mediaData}`
    : undefined;

  const handleChooseFile = () => inputRef.current?.click();

  const handleRemove = async () => {
    if (!domainId) {
      return;
    }
    setPendingAction('remove');
    try {
      await saveExportConfig({
        domainId,
        logo: null,
        name: config?.name,
        items: config?.items ?? [],
      }).unwrap();
      setErrorMessage('');
    } catch {
      setErrorMessage(t('logoUploadFailed'));
    } finally {
      setPendingAction(null);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !domainId) {
      return;
    }

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setErrorMessage(t('logoInvalidFormat'));
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setErrorMessage(t('logoFileTooLarge'));
      return;
    }

    try {
      const { width, height } = await getImageDimensions(file);
      if (width > MAX_DIMENSION_PX || height > MAX_DIMENSION_PX) {
        setErrorMessage(t('logoDimensionsTooLarge'));
        return;
      }
    } catch {
      setErrorMessage(t('logoInvalidFormat'));
      return;
    }

    setPendingAction('save');
    try {
      const mediaData = await readFileAsBase64(file);
      await saveExportConfig({
        domainId,
        logo: { name: file.name, mediaType: file.type, mediaData },
        name: config?.name,
        items: config?.items ?? [],
      }).unwrap();
      setErrorMessage('');
    } catch {
      setErrorMessage(t('logoUploadFailed'));
    } finally {
      setPendingAction(null);
    }
  };

  const isBusy = pendingAction !== null || !domainId;

  return (
    <div className={cls.logoImport}>
      <div className={cls.mark}>
        {previewUrl ? (
          <img
            src={previewUrl}
            alt=''
            className={cls.previewImage}
            data-testid='logo-import-preview'
          />
        ) : (
          <Icon pathToIcon={ICONS.imageWhite} size={20} />
        )}
      </div>
      <div className={cls.body}>
        <span className={cls.name}>{t('logoForTheReport')}</span>
        {previewUrl ? (
          <div className={cls.actions}>
            <Button
              theme='text'
              tone='default'
              size='xs'
              shape='round'
              onClick={handleChooseFile}
              disabled={isBusy}
              loading={pendingAction === 'save'}
            >
              {t('replace')}
            </Button>
            <Button
              theme='text'
              tone='danger'
              size='xs'
              shape='round'
              onClick={handleRemove}
              disabled={isBusy}
              loading={pendingAction === 'remove'}
            >
              {t('remove')}
            </Button>
          </div>
        ) : (
          <Button
            theme='text'
            tone='default'
            size='xs'
            shape='round'
            onClick={handleChooseFile}
            disabled={isBusy}
            loading={pendingAction === 'save'}
          >
            {t('chooseFile')}
          </Button>
        )}
        {errorMessage && (
          <span className={cls.errorMessage}>{errorMessage}</span>
        )}
      </div>
      <input
        ref={inputRef}
        type='file'
        accept='image/png,image/jpeg'
        hidden
        onChange={handleFileChange}
        data-testid='logo-import-file-input'
      />
    </div>
  );
};
